import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import Spiner from '../../component/Spiner'
import ListEmptyState from '../../component/ListEmptyState'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'
import {
    applyAppCompanyDomainDraft,
    clearAppCompanyDomainDraft,
    createAppCompanyDomainDraftPayload,
    createEditAppCompanyDomainDraftKey,
    readAppCompanyDomainDraft,
    saveAppCompanyDomainDraft,
} from './appCompanyDomainDraftUtils'

// 상세 조회 실패 시에도 폼 기본값을 명확하게 되돌릴 수 있게 공통 초기값을 사용한다.
const createDefaultParams = () => ({
    companyId: 0,
    domain: '',
})

export default function AppCompanyDomain(props) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const id = props.match.params.companyDomainId
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || { searchType: 0 }
    const listSearch = location.search || ''
    const draftKey = createEditAppCompanyDomainDraftKey(id)
    const [loading, setLoading] = useState(true)
    const [hasDetail, setHasDetail] = useState(false)
    const [saving, setSaving] = useState(false)
    const [draftReady, setDraftReady] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams())
    const [companyList, setCompanyList] = useState([])
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const initialDraftSnapshotRef = useRef('')

    const normalizeDomain = (domain) => (domain || '').trim().replace(/^@+/, '')
    const isValidDomain = (domain) => domain.includes('.') && !domain.includes('@')

    const getCompanyList = useCallback(async () => {
        try {
            const { data } = await API.post('/api/v1/admin/appCompany/getCompanyList', {})
            if (data.code === 200) {
                setCompanyList(data.data)
                return true
            }

            if (data.code === 403) {
                history.push('/login')
                return false
            }

            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(data.message)
            return false
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업 목록을 불러오지 못했습니다.')
            return false
        }
    }, [history])

    const getData = useCallback(async () => {
        if (!id) {
            const errorMessage = '필수 파라미터가 없어 기업 도메인 상세를 불러올 수 없습니다.'
            setParams(createDefaultParams())
            setCompanyList([])
            setHasDetail(false)
            setDraftReady(false)
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
            setLoading(false)
            return
        }

        setLoading(true)
        setHasDetail(false)
        setDraftReady(false)
        setDetailErrorMessage('')

        try {
            const companyLoaded = await getCompanyList()
            if (!companyLoaded) {
                setParams(createDefaultParams())
                setDetailErrorMessage('기업 목록을 불러오지 못해 도메인 상세를 표시할 수 없습니다.')
                return
            }

            const { data } = await API.post('/api/v1/admin/appCompany/getDomainDetail', { companyDomainId: id })
            if (data.code === 200) {
                const baseParams = data.data || {}

                if (!baseParams.companyDomainId) {
                    setParams(createDefaultParams())
                    setDetailErrorMessage('기업 도메인 상세 정보를 찾을 수 없습니다.')
                    setLoading(false)
                    return
                }

                const storedDraft = readAppCompanyDomainDraft(draftKey)
                const restoredDraft = applyAppCompanyDomainDraft({
                    baseParams,
                    draft: storedDraft,
                })

                initialDraftSnapshotRef.current = JSON.stringify(createAppCompanyDomainDraftPayload({
                    params: baseParams,
                }))

                setParams(restoredDraft.params)
                setHasDetail(true)
                setDetailErrorMessage('')
                setDraftReady(true)
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '기업 도메인 상세를 불러오지 못했습니다.'
                setParams(createDefaultParams())
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '기업 도메인 상세를 불러오지 못했습니다.'
            setParams(createDefaultParams())
            setDetailErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [draftKey, getCompanyList, history, id])

    useEffect(() => {
        getData()
    }, [getData])

    useEffect(() => {
        if (!draftReady || !hasDetail) {
            return
        }

        // 기업 도메인 수정은 목록 query를 건드리지 않고 폼 draft만 sessionStorage에 보존한다.
        const nextDraft = createAppCompanyDomainDraftPayload({
            params,
        })
        const nextDraftSnapshot = JSON.stringify(nextDraft)

        if (nextDraftSnapshot === initialDraftSnapshotRef.current) {
            clearAppCompanyDomainDraft(draftKey)
            return
        }

        saveAppCompanyDomainDraft(draftKey, nextDraft)
    }, [draftKey, draftReady, hasDetail, params])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const handleUpdateField = (e) => {
        setParams((prev) => ({
            ...prev,
            [e.target.name]: e.target.value
        }))
    }

    const moveToList = (snackbarState) => {
        clearAppCompanyDomainDraft(draftKey)
        history.push(createListLinkTarget({
            pathname: `/appCompanyDomains`,
            search: listSearch,
            ...(snackbarState ? {
                openSnackbar: true,
                severitySnackbar: snackbarState.severity,
                messageSnackbar: snackbarState.message,
            } : {}),
            searchText: searchText,
            searchSelect: searchSelect,
            curPage: curPage
        }))
    }

    const updateData = async (payload) => {
        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appCompany/updateDomain', payload)
            if (data.code === 200) {
                // 저장 후에도 운영자가 보던 기업 도메인 목록 조건으로 그대로 복귀시킨다.
                moveToList({
                    severity: 'success',
                    message: data.message,
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업 도메인을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const nextDomain = normalizeDomain(params.domain)

        setParams((prev) => ({
            ...prev,
            domain: nextDomain
        }))

        if (!params.companyId || Number(params.companyId) === 0) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('기업을 선택하세요.')
            return
        }

        if (!nextDomain || !isValidDomain(nextDomain)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('도메인 형식이 잘못되었습니다.')
            return
        }

        updateData({
            companyDomainId: id,
            companyId: params.companyId,
            domain: nextDomain
        })
    }

    // 상세 조회 실패 시 목록 화면과 같은 오류 빈 상태 안내를 사용한다.
    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="기업 도메인 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={(
                <BottomButtonGroup variant="emptyStateAction" path="/appCompanyDomains" search={listSearch} visible={false} searchText={searchText} searchSelect={searchSelect} curPage={curPage} onBackClick={() => clearAppCompanyDomainDraft(draftKey)} backLabel="목록으로"/>
            )}
            sx={{ mt: 2 }}
        />
    )

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                !hasDetail ? (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>기업 도메인 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
                ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>기업 도메인 수정</Title>
                    <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} >
                        <Grid container spacing={2} sx={{ marginTop: 2 }}>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="기업"
                                        select
                                        id="companyId"
                                        name="companyId"
                                        value={params.companyId}
                                        size="small"
                                        sx={{ minWidth: 150 }}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                    >
                                        <MenuItem value={0}>--- 선택하세요 ---</MenuItem>
                                        {companyList.map((item) => (
                                            <MenuItem key={item.companyId} value={item.companyId}>
                                                {item.companyName}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12} md={12}>
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        label="도메인 @다음부터 입력해주세요"
                                        id="domain"
                                        name="domain"
                                        value={params.domain || ''}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                        <BottomButtonGroup
                            path="/appCompanyDomains"
                            search={listSearch}
                            visible={true}
                            searchText={searchText}
                            searchSelect={searchSelect}
                            curPage={curPage}
                            submitDisabled={saving}
                            submitLabel={saving ? '저장 중...' : '저장'}
                            onBackClick={() => clearAppCompanyDomainDraft(draftKey)}
                        />
                    </Box>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
                )
            )}
        </Grid>
    )
}
